import { useMemo, useCallback } from 'react';
import Button from '@ingka/button';
import Tooltip from '@ingka/tooltip';
import cx from 'classnames';
import { useDispatch, useSelector } from 'react-redux';
import { createNewCategory } from '../../store/actions/category/createNewCategory';
import { selectSourceTree } from '../../store/actions/data/selectSourceTree';
import type { TreePanes } from '../../store/slices/pane';
import { togglePane } from '../../store/slices/pane';
import { newCategoryEnabledSelector } from '../../store/selectors/category/ui';
import { selectCurrentMarketCode } from '../../store/slices/intl';
import { selectSearchPanelOpenForTree } from '../../store/slices/treeSearch';
import { resetTreeSorting, selectSortedCatalogs } from '../../store/slices/treeSort';
import { catalogConfigSelector } from '../../store/selectors/tree/data';
import { isMasterCatalog, STATUS } from '../../utils/catalog/catalog';
import { makeTreeId } from '../../utils/tree/ids';
import Pane from '../Pane';
import Tree from '../Tree/Tree';
import SearchPanel from './SearchPanel/SearchPanel';
import TreeSelector from './TreeSelector/TreeSelector';
import { saveCategorySorting } from '../../store/actions/category/saveCategorySorting';
import { trackUIInteraction } from '../../utils/analyticsEvents';
import informationCircle from '@ingka/ssr-icon/paths/information-circle';
import checkmarkCircle from '@ingka/ssr-icon/paths/checkmark-circle';
import arrowCounterclockwise from '@ingka/ssr-icon/paths/arrow-counterclockwise';
import createNew from '@ingka/ssr-icon/paths/create-new';

import styles from './TreePane.module.scss';
import SearchToggler from './SearchToggler';
import ExpandToggler from './ExpandToggler';
import Header from '../Pane/Header';
import type { Catalog } from '../../entities/Catalog';
import type { AppThunkDispatch, RootState } from '../../store';

const INVALID = 'INVALID';

type TreePaneProps = {
  active: boolean;
  catalog: Catalog;
  className: string;
  collapsed: boolean;
  openDetails: boolean;
  pane: TreePanes;
};

const TreePane = function TreePane({
  active,
  catalog,
  className,
  collapsed,
  openDetails,
  pane,
}: TreePaneProps) {
  const dispatch = useDispatch<AppThunkDispatch>();
  const sortedCatalogs = useSelector(selectSortedCatalogs);
  const market = useSelector(selectCurrentMarketCode);
  const sourceId = useMemo(() => catalog?.catalogId || INVALID, [catalog]);
  const tree = useMemo(() => makeTreeId(pane, sourceId), [pane, sourceId]);
  const { newCategory: showCreateNewCategory } = useSelector((state: RootState) =>
    catalogConfigSelector(state, sourceId),
  );
  const createNewCategoryDisabled = useSelector(
    (state: RootState) =>
      !(showCreateNewCategory && newCategoryEnabledSelector(state, tree)),
  );

  const createNewCategoryAction = useCallback(() => {
    trackUIInteraction('create_new_category');
    dispatch(createNewCategory());
  }, [dispatch]);

  const selectSourceAction = useCallback(
    (catalogId: string) => {
      dispatch(selectSourceTree(pane, catalogId));
    },
    [dispatch, pane],
  );

  const openDetailsAction = useCallback(() => {
    trackUIInteraction('properties_pane_open');
    dispatch(togglePane(true));
  }, [dispatch]);
  const searchPanelOpen = useSelector((state: RootState) =>
    selectSearchPanelOpenForTree(state, tree),
  );
  const isDraftCatalog = useMemo(
    () => isMasterCatalog(catalog, STATUS.DRAFT),
    [catalog],
  );

  const treeKey = useMemo(
    () => `${market}-${pane}-${sourceId}`,
    [market, pane, sourceId],
  );

  const onSaveCategorySorting = useCallback(
    () => dispatch(saveCategorySorting(sourceId)),
    [dispatch, sourceId],
  );

  const onClearSorting = useCallback(
    () => dispatch(resetTreeSorting({ catalogId: sourceId })),
    [dispatch, sourceId],
  );

  const sortingButtons = useMemo(
    () =>
      sortedCatalogs[sourceId] && Object.keys(sortedCatalogs[sourceId]).length > 0
        ? [
            <Tooltip
              className={styles.sortingButton}
              describedById="save-sorting-tooltip"
              interactiveCustomTrigger
              key="save-sorting-tooltip"
              tooltipText="Save sorting"
              position="bottom"
            >
              <Button
                className={styles.treePaneButtonBlue}
                key="save-sort"
                ssrIcon={checkmarkCircle}
                iconOnly
                size="small"
                aria-label="Save sorting"
                type="tertiary"
                onClick={onSaveCategorySorting}
              />
            </Tooltip>,
            <Tooltip
              className={styles.sortingButton}
              describedById="undo-sorting-tooltip"
              interactiveCustomTrigger
              key="undo-sorting-tooltip"
              tooltipText="Undo sorting"
              position="bottom"
            >
              <Button
                key="delete-sort"
                ssrIcon={arrowCounterclockwise}
                type="tertiary"
                iconOnly
                size="small"
                aria-label="Undo sorting"
                onClick={onClearSorting}
              />
              ,
            </Tooltip>,
          ]
        : [],
    [sortedCatalogs, onClearSorting, onSaveCategorySorting, sourceId],
  );

  return (
    <Pane id={pane} className={className} collapsed={collapsed}>
      <Header
        buttons={[
          ...sortingButtons,
          showCreateNewCategory && (
            <Tooltip
              describedById="new-category-tooltip"
              interactiveCustomTrigger
              key="new-cateogory-tooltip"
              tooltipText="New category"
            >
              <Button
                key="new-category"
                onClick={createNewCategoryAction}
                size="small"
                iconOnly
                type="tertiary"
                ssrIcon={createNew}
                aria-label="New category"
                disabled={createNewCategoryDisabled}
              />
            </Tooltip>
          ),
          <SearchToggler key="search" tree={tree} />,
          <ExpandToggler key="size" pane={pane} checked={active} />,
          openDetails && <div className={styles.divider} key="divider" />,
          openDetails && (
            <Tooltip
              describedById="show-details-tooltip"
              interactiveCustomTrigger
              key="show-details-tooltip"
              tooltipText="Show details"
            >
              <Button
                key="info"
                size="small"
                iconOnly
                type="tertiary"
                onClick={openDetailsAction}
                ssrIcon={informationCircle}
                aria-label="Show details pane"
              />
            </Tooltip>
          ),
        ]}
      >
        <TreeSelector
          pane={pane}
          key={`${market}-${pane}-${sourceId}`}
          id={`${market}-select-${pane}`}
          sourceId={sourceId}
          onChange={selectSourceAction}
        />
        {searchPanelOpen && <SearchPanel tree={tree} />}
      </Header>
      <div
        className={cx(
          styles.content,
          { [styles.dashed]: isDraftCatalog },
          className,
        )}
      >
        <Tree pane={pane} key={treeKey} catalog={catalog} tree={tree} />
      </div>
    </Pane>
  );
};

export default TreePane;
